import { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface TWRRangePickerProps {
  enabled: boolean;
  range: DateRange | undefined;
  formattedRange: string;
  popoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  onEnableChange: (enabled: boolean) => void;
  onRangeChange: (range: DateRange | undefined) => void;
}

export function TWRRangePicker({
  enabled,
  range,
  formattedRange,
  popoverOpen,
  onPopoverOpenChange,
  onEnableChange,
  onRangeChange,
}: TWRRangePickerProps) {
  return (
    <Popover open={popoverOpen} onOpenChange={onPopoverOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant={enabled ? "default" : "outline"}
          size="sm"
          className={cn(
            "h-8 gap-2 text-xs font-medium",
            enabled && "bg-primary/90 hover:bg-primary"
          )}
          onClick={() => {
            if (!enabled) {
              onEnableChange(true);
            }
            onPopoverOpenChange(true);
          }}
        >
          <CalendarDays className="h-3.5 w-3.5" />
          {enabled && range?.from 
            ? formattedRange
            : "Custom Range"
          }
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="p-3 border-b border-border/50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Select Date Range</span>
            {enabled && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  onEnableChange(false);
                  onRangeChange(undefined);
                  onPopoverOpenChange(false);
                }}
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
        <Calendar
          mode="range"
          selected={range}
          onSelect={(newRange) => {
            onRangeChange(newRange);
            // Close popover after selecting end date
            if (newRange?.from && newRange?.to) {
              onPopoverOpenChange(false);
            }
          }}
          numberOfMonths={2}
          disabled={{ after: new Date() }}
        />
      </PopoverContent>
    </Popover>
  );
}
